import { LitElement, html, css, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";

import * as store from "../state/store.js";
import { SignalWatcher } from "../state/signal-watcher.js";
import { closeContextMenu, openModal } from "../ops/ui.js";
import { requestLayerRename } from "../ops/layers.js";
import { resetView } from "../ops/io.js";
import {
  deselectAll,
  deselectAllInLayer,
  invertSelection,
  invertSelectionInLayer,
  moveSelectedToLayer,
  selectAll,
  selectAllInLayer,
  selectAllInnerCutouts,
  selectAllRoundHoles,
  selectAllSimilarTo,
} from "../ops/entities.js";
import { isClosedContour } from "../utils/geometry.js";

const MENU_WIDTH = 240;   // matches .menu min-width; used for submenu placement
const SUBMENU_WIDTH = 220;

@customElement("context-menu")
export class ContextMenu extends SignalWatcher(LitElement) {
  static override styles = css`
    :host {
      position: fixed;
      z-index: 1000;
      pointer-events: none;
      font-family: var(--font-body);
    }

    .menu {
      background: var(--bg-tertiary);
      border: 1px solid var(--border-color);
      border-radius: 6px;
      padding: 4px 0;
      min-width: ${MENU_WIDTH}px;
      box-shadow: 0 8px 30px var(--shadow);
      pointer-events: auto;
    }

    .item {
      padding: 6px 16px;
      cursor: pointer;
      font-size: 12px;
      color: var(--text-primary);
      display: flex;
      align-items: center;
      gap: 8px;
      position: relative;
    }

    .item:hover { background: var(--bg-selected); }
    .item.disabled { opacity: 0.4; cursor: not-allowed; }
    .item.disabled:hover { background: transparent; }

    .icon { width: 20px; text-align: center; font-size: 14px; }

    .shortcut {
      margin-left: auto;
      color: var(--text-secondary);
      font-size: 10px;
    }

    .check { margin-left: auto; color: var(--accent); font-size: 13px; }

    .arrow {
      margin-left: auto;
      color: var(--text-secondary);
      font-size: 11px;
    }

    .divider {
      height: 1px;
      background: var(--border-color);
      margin: 4px 8px;
    }

    /* ---- submenu ---- */
    .has-submenu > .submenu {
      display: none;
    }
    /* Keep the submenu open while the pointer is over EITHER the parent item
       or the submenu itself. That's what the outer :hover on the wrapper below
       gives us — the parent item's :hover state persists across its children. */
    .has-submenu:hover > .submenu {
      display: block;
    }
    .has-submenu.disabled:hover > .submenu {
      display: none;
    }

    .submenu {
      position: absolute;
      top: -4px;
      min-width: ${SUBMENU_WIDTH}px;
      max-height: 320px;
      overflow-y: auto;
      background: var(--bg-tertiary);
      border: 1px solid var(--border-color);
      border-radius: 6px;
      padding: 4px 0;
      box-shadow: 0 8px 30px var(--shadow);
    }

    /* Default: submenu opens to the right of its parent item. */
    .submenu.right { left: 100%; margin-left: 2px; }
    /* Flip to the left when there isn't room on the right. */
    .submenu.left  { right: 100%; margin-right: 2px; }

    .submenu::-webkit-scrollbar { width: 6px; }
    .submenu::-webkit-scrollbar-track { background: var(--scrollbar-track); }
    .submenu::-webkit-scrollbar-thumb {
      background: var(--scrollbar-thumb);
      border-radius: 2px;
    }

    .submenu .item .icon.dot {
      color: var(--accent);
    }

    /* ---- mobile: bottom action sheet ----
       On mobile the menu becomes a full-width sheet anchored to the bottom
       of the viewport with a scrim, larger tap targets, and the hover
       submenu switches to an inline accordion (see .expanded below). */
    :host(.mobile) {
      position: fixed;
      inset: 0;
      left: 0 !important;
      top: 0 !important;
      pointer-events: auto;
      background: var(--scrim, rgba(0, 0, 0, 0.5));
      display: flex;
      align-items: flex-end;
      justify-content: center;
      animation: sheet-scrim-in 180ms ease-out;
    }
    @keyframes sheet-scrim-in {
      from { background-color: transparent; }
      to   { background-color: var(--scrim, rgba(0, 0, 0, 0.5)); }
    }
    :host(.mobile) .menu {
      width: 100%;
      max-width: 640px;
      min-width: 0;
      padding: 8px 0 calc(12px + var(--safe-bottom, 0px));
      border-radius: 16px 16px 0 0;
      background: var(--sheet-bg, var(--bg-tertiary));
      backdrop-filter: blur(12px);
      animation: menu-sheet-in 220ms cubic-bezier(0.22, 1, 0.36, 1);
    }
    @keyframes menu-sheet-in {
      from { transform: translateY(100%); }
      to   { transform: translateY(0); }
    }
    :host(.mobile) .menu::before {
      content: "";
      display: block;
      width: 44px;
      height: 4px;
      border-radius: 2px;
      background: var(--border-color);
      opacity: 0.9;
      margin: 4px auto 8px;
    }
    :host(.mobile) .item {
      padding: 14px 20px;
      font-size: 15px;
      min-height: 52px;
      gap: 14px;
    }
    :host(.mobile) .item .icon {
      width: 24px;
      font-size: 18px;
    }
    :host(.mobile) .item .shortcut {
      font-size: 11px;
    }
    :host(.mobile) .divider {
      margin: 4px 16px;
    }
    /* Accordion-style submenu on mobile — expands inline underneath the
       parent instead of floating to the side. Rows share the same 52px
       tap target. */
    :host(.mobile) .has-submenu > .submenu {
      display: none;
      position: static;
      min-width: 0;
      max-height: 240px;
      margin: 0 8px 4px;
      border-radius: 8px;
      background: var(--bg-secondary);
    }
    :host(.mobile) .has-submenu.open > .submenu {
      display: block;
    }
    :host(.mobile) .has-submenu:hover > .submenu {
      display: none; /* Kill the hover-open on touch. */
    }
    :host(.mobile) .has-submenu.open > .submenu {
      display: block;
    }
    :host(.mobile) .has-submenu > .arrow {
      transition: transform 0.18s ease;
    }
    :host(.mobile) .has-submenu.open > .arrow {
      transform: rotate(90deg);
    }
  `;

  // Accordion state for the mobile "Move to Layer" nested list. Kept
  // ephemeral (not in the store) — a fresh open resets it via #resetMobile.
  @state()
  private mobileMoveOpen = false;

  #resetMobile = () => {
    this.mobileMoveOpen = false;
  };

  override render() {
    const cm = store.contextMenu();
    const mobile = store.isMobile.value;
    if (!cm.open) {
      this.classList.remove("mobile");
      this.#resetMobile();
      return nothing;
    }

    this.classList.toggle("mobile", mobile);

    // Positioning: on mobile the menu is a bottom sheet — its host
    // covers the viewport (see :host(.mobile) rules), so we ignore
    // click coordinates. On desktop, clamp to the window so the menu
    // never spills.
    const left = mobile ? 0 : Math.min(cm.x, window.innerWidth - MENU_WIDTH - 8);
    const top = mobile ? 0 : Math.min(cm.y, window.innerHeight - 400);
    this.style.left = `${left}px`;
    this.style.top = `${top}px`;

    // Layer-scoped variant: right-click on a layer row in the sidebar. Shows a
    // trimmed menu of layer-scoped actions instead of the general canvas menu.
    if (cm.layer) {
      const layer = store.layers()[cm.layer];
      const locked = !!layer?.locked;
      const count = layer?.entityIds.length ?? 0;
      const layerHasSelection = layer
        ? layer.entityIds.some((id) => store.selectedEntityIds().has(id))
        : false;
      return html`
        <div class="menu" @click=${(e: Event) => e.stopPropagation()}>
          <div class="item disabled" style="opacity: 0.6;">
            <span class="icon">📋</span>
            <span>Layer: ${cm.layer}</span>
            <span class="shortcut">${count}</span>
          </div>
          <div class="divider"></div>
          ${cm.layer !== "0"
            ? item(
                "✎",
                "Rename Layer",
                null,
                () => requestLayerRename(cm.layer!),
                locked
              )
            : nothing}
          ${item(
            "⬜",
            "Select All in Layer",
            null,
            () => selectAllInLayer(cm.layer!),
            locked || count === 0
          )}
          ${layerHasSelection && !locked
            ? item(
                "⇋",
                "Select All Invert in Layer",
                null,
                () => invertSelectionInLayer(cm.layer!)
              )
            : nothing}
          ${item(
            "⬛",
            "Deselect All in Layer",
            null,
            () => deselectAllInLayer(cm.layer!),
            count === 0
          )}
          <div class="divider"></div>
          ${item(
            "⊙",
            "Select All Round Holes in Layer",
            null,
            () => selectAllRoundHoles(cm.layer!),
            locked || count === 0
          )}
          ${item(
            "◱",
            "Select All Inner-Cutouts in Layer",
            null,
            () => selectAllInnerCutouts(cm.layer!),
            locked || count === 0
          )}
        </div>
      `;
    }

    // Pick the submenu side so it doesn't spill off-screen.
    const submenuSide: "right" | "left" =
      left + MENU_WIDTH + SUBMENU_WIDTH + 12 <= window.innerWidth
        ? "right"
        : "left";

    // Canvas menu was pruned to just the actions that only make sense on
    // the canvas surface: fit the view, act on the current selection, move
    // it between layers. File / theme / panel / grid toggles all live in
    // the topbar already, and duplicating them here just added clutter.
    const selectedIds = store.selectedEntityIds();
    const hasSelection = selectedIds.size > 0;
    const layers = store.layerOrder();
    const anchorLayer = store.selectedLayer();

    // "Select All Similar Cutouts" is only shown when the selection is a
    // single closed contour -- otherwise there's no unambiguous anchor to
    // match against. Anchor id captured here so the menu handler doesn't
    // race with a selection change between render and click.
    let similarAnchorId: number | null = null;
    if (selectedIds.size === 1) {
      const only = selectedIds.values().next().value as number;
      const anchor = store.entities().find((e) => e.id === only);
      if (anchor && isClosedContour(anchor)) similarAnchorId = only;
    }

    return html`
      <div
        class="menu"
        @click=${(e: Event) => e.stopPropagation()}
      >
        ${item("🔍", "Fit to Window", mobile ? null : "Ctrl+R", () => resetView())}
        <div class="divider"></div>
        ${moveToLayerItem(
          hasSelection,
          layers,
          anchorLayer,
          submenuSide,
          mobile,
          this.mobileMoveOpen,
          () => {
            this.mobileMoveOpen = !this.mobileMoveOpen;
          }
        )}
        ${item("⬜", "Select All", mobile ? null : "Ctrl+A", () => selectAll())}
        ${hasSelection
          ? item("⇋", "Select All Invert", null, () => invertSelection())
          : nothing}
        ${item("⬛", "Deselect All", mobile ? null : "Esc", () => deselectAll())}
        <div class="divider"></div>
        ${item("⊙", "Select All Round Holes", null, () => selectAllRoundHoles())}
        ${item("◱", "Select All Inner-Cutouts", null, () => selectAllInnerCutouts())}
        ${similarAnchorId != null
          ? item("≡", "Select All Similar Cutouts", null, () =>
              selectAllSimilarTo(similarAnchorId!)
            )
          : nothing}
      </div>
    `;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    // On mobile the scrim IS the host. Tapping outside the .menu should
    // dismiss — clicks on any child .menu already stop propagation, so
    // whatever bubbles up here came from the scrim.
    this.addEventListener("click", this.#onHostClick);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener("click", this.#onHostClick);
  }

  #onHostClick = () => {
    if (this.classList.contains("mobile")) closeContextMenu();
  };
}

function item(
  icon: string,
  label: string,
  shortcut: string | null,
  onClick: () => void,
  disabled = false
) {
  return html`
    <div
      class="item ${disabled ? "disabled" : ""}"
      @click=${() => {
        if (disabled) return;
        onClick();
        closeContextMenu();
      }}
    >
      <span class="icon">${icon}</span>
      <span>${label}</span>
      ${shortcut ? html`<span class="shortcut">${shortcut}</span>` : ""}
    </div>
  `;
}

// The Move-to-Layer entry.
//
// Desktop: hover shows a nested list of every layer plus a "+ New layer..."
// action. Direct click on a layer moves the current selection there.
//
// Mobile: hover doesn't exist. Tap the parent to expand the accordion
// (`.has-submenu.open > .submenu` in the styles); the same tap-to-move
// semantics apply on child rows. The chevron rotates 90° when expanded.
function moveToLayerItem(
  hasSelection: boolean,
  layers: string[],
  anchor: string | null,
  side: "right" | "left",
  mobile: boolean,
  isOpen: boolean,
  toggleOpen: () => void
) {
  const disabled = !hasSelection || layers.length === 0;
  const openClass = mobile && isOpen ? "open" : "";

  return html`
    <div
      class="item has-submenu ${disabled ? "disabled" : ""} ${openClass}"
      @click=${(e: Event) => {
        e.stopPropagation();
        if (disabled) return;
        // On mobile the accordion opens on parent tap. Desktop retains its
        // hover behavior — nothing to do here except swallow the click so
        // the outer menu doesn't close.
        if (mobile) toggleOpen();
      }}
    >
      <span class="icon">📎</span>
      <span>Move to Layer</span>
      <span class="arrow">▸</span>
      ${disabled
        ? nothing
        : html`
            <div class="submenu ${side}">
              ${layers.map(
                (name) => html`
                  <div
                    class="item"
                    @click=${(e: Event) => {
                      e.stopPropagation();
                      moveSelectedToLayer(name);
                      closeContextMenu();
                    }}
                  >
                    <span class="icon ${name === anchor ? "dot" : ""}"
                      >${name === anchor ? "•" : ""}</span
                    >
                    <span>${name}</span>
                  </div>
                `
              )}
              <div class="divider"></div>
              <div
                class="item"
                @click=${(e: Event) => {
                  e.stopPropagation();
                  openModal("move-to-new-layer", {});
                  closeContextMenu();
                }}
              >
                <span class="icon">+</span>
                <span>New layer...</span>
              </div>
            </div>
          `}
    </div>
  `;
}

declare global {
  interface HTMLElementTagNameMap {
    "context-menu": ContextMenu;
  }
}
